import { useMemo, type MouseEvent } from "react";
import clsx from "clsx";
import {
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { SupplierLogo } from "@/components/GeschaeftspartnerLogo";
import { Button } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import type { AnfrageListItem } from "@/lib/anfragen";
import { formatAuftragsnummerDisplay } from "@/lib/orderDraftUtils";
import {
  ANFRAGE_STAGE_ORDER,
  anfrageStage,
  fmtDateTime,
  fmtRelativeDays,
  stageAccentClass,
  stageBadgeClass,
  stageLabel,
  stagePrimaryAction,
  type AnfrageStage,
  uniqueTaNummern,
} from "@/pages/anfragen/anfrageWizardUtils";

export type AnfragenView = "offen" | "erledigt" | "alle";

const VIEW_OPTIONS: { value: AnfragenView; label: string }[] = [
  { value: "offen", label: "Offen" },
  { value: "erledigt", label: "Erledigt" },
  { value: "alle", label: "Alle" },
];

const GROUP_META: Record<AnfrageStage, { title: string; hint: string }> = {
  answered: {
    title: "Antwort erhalten",
    hint: "Der Lieferant hat geantwortet – Preise prüfen, bestellen oder als erledigt markieren.",
  },
  ready: {
    title: "Noch nicht versendet",
    hint: "Diese Anfragen sind fertig, aber noch nicht beim Lieferanten.",
  },
  draft: {
    title: "Entwürfe",
    hint: "Angefangen, aber noch nicht abgeschickt.",
  },
  waiting: {
    title: "Wartet auf Antwort",
    hint: "Versendet – Antworten aus dem Postfach erscheinen hier automatisch.",
  },
  ordered: {
    title: "Bestellt",
    hint: "Aus diesen Anfragen wurde bereits eine Bestellung erzeugt.",
  },
  done: {
    title: "Erledigt",
    hint: "",
  },
};

function StageIcon({ stage, className }: { stage: AnfrageStage; className?: string }) {
  const cls = clsx("h-5 w-5", className);
  switch (stage) {
    case "draft":
      return <DocumentTextIcon className={cls} aria-hidden />;
    case "ready":
      return <PaperAirplaneIcon className={cls} aria-hidden />;
    case "waiting":
      return <ClockIcon className={cls} aria-hidden />;
    case "answered":
      return <ChatBubbleLeftEllipsisIcon className={cls} aria-hidden />;
    case "ordered":
      return <ShoppingCartIcon className={cls} aria-hidden />;
    case "done":
      return <CheckCircleIcon className={cls} aria-hidden />;
  }
}

function auftragLabels(row: AnfrageListItem): string[] {
  const fromAuftraege = (row.auftraege || [])
    .map((a) => a.auftragsnummer_display || formatAuftragsnummerDisplay(a) || a.auftragsnummer || "")
    .filter(Boolean);
  if (fromAuftraege.length) return fromAuftraege;
  const seen = new Set<string>();
  for (const p of row.positionen || []) {
    const label = p.auftragsnummer_display || p.auftragsnummer || "";
    if (label) seen.add(label);
  }
  return [...seen];
}

export interface AnfragenListActions {
  onOpen: (row: AnfrageListItem) => void;
  onOpenDraft: (row: AnfrageListItem) => void;
  onSend: (row: AnfrageListItem) => void;
  onNew: () => void;
}

export function AnfragenListSection({
  rows,
  loading,
  search,
  view,
  mailCounts,
  onSearchChange,
  onViewChange,
  actions,
}: {
  rows: AnfrageListItem[];
  loading?: boolean;
  search: string;
  view: AnfragenView;
  mailCounts: Record<string, number>;
  onSearchChange: (value: string) => void;
  onViewChange: (value: AnfragenView) => void;
  actions: AnfragenListActions;
}) {
  const grouped = useMemo(() => {
    const map = new Map<AnfrageStage, AnfrageListItem[]>();
    for (const row of rows) {
      const stage = anfrageStage(row, mailCounts[String(row.id)]);
      const list = map.get(stage);
      if (list) list.push(row);
      else map.set(stage, [row]);
    }
    return ANFRAGE_STAGE_ORDER.filter((s) => map.has(s)).map((stage) => ({
      stage,
      rows: map.get(stage) || [],
    }));
  }, [rows, mailCounts]);

  const showGroups = view === "offen" && !search.trim();

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-ios border border-separator bg-card p-3 shadow-ios">
        <SegmentedControl
          value={view}
          onChange={onViewChange}
          options={VIEW_OPTIONS}
          name="Anfragen filtern"
          size="md"
        />
        <label className="relative min-w-[14rem] flex-1">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
            aria-hidden
          />
          <input
            type="search"
            className="eh-input w-full ps-9"
            placeholder="Lieferant, TA-Nummer, Auftrag, Artikel …"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Anfragen durchsuchen"
          />
        </label>
      </div>

      {loading && !rows.length ? (
        <ul className="space-y-2" aria-busy>
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-24 animate-pulse rounded-ios border border-separator bg-card/70" />
          ))}
        </ul>
      ) : !rows.length ? (
        <EmptyState view={view} search={search} onNew={actions.onNew} />
      ) : showGroups ? (
        <div className="space-y-6">
          {grouped.map(({ stage, rows: groupRows }) => (
            <section key={stage} aria-labelledby={`anfragen-group-${stage}`}>
              <header className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 px-1">
                <h2 id={`anfragen-group-${stage}`} className="flex items-center gap-2 text-sm font-semibold text-label">
                  <span
                    className={clsx(
                      "inline-flex h-6 w-6 items-center justify-center rounded-full",
                      stageBadgeClass(stage),
                    )}
                  >
                    <StageIcon stage={stage} className="h-3.5 w-3.5" />
                  </span>
                  {GROUP_META[stage].title}
                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label-secondary">
                    {groupRows.length}
                  </span>
                </h2>
                {GROUP_META[stage].hint ? (
                  <p className="text-xs text-label-tertiary">{GROUP_META[stage].hint}</p>
                ) : null}
              </header>
              <ul className="space-y-2" role="list">
                {groupRows.map((row) => (
                  <AnfrageRow
                    key={row.id}
                    row={row}
                    stage={stage}
                    pendingCount={mailCounts[String(row.id)] ?? row.pending_mail_count ?? 0}
                    actions={actions}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <ul className="space-y-2" role="list">
          {rows.map((row) => {
            const stage = anfrageStage(row, mailCounts[String(row.id)]);
            return (
              <AnfrageRow
                key={row.id}
                row={row}
                stage={stage}
                pendingCount={mailCounts[String(row.id)] ?? row.pending_mail_count ?? 0}
                actions={actions}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

function EmptyState({
  view,
  search,
  onNew,
}: {
  view: AnfragenView;
  search: string;
  onNew: () => void;
}) {
  if (search.trim()) {
    return (
      <div className="rounded-ios border border-dashed border-separator-opaque bg-card p-8 text-center text-sm text-label-secondary shadow-ios">
        Keine Anfrage passt zu „{search.trim()}“.
      </div>
    );
  }
  if (view === "erledigt") {
    return (
      <div className="rounded-ios border border-dashed border-separator-opaque bg-card p-8 text-center text-sm text-label-secondary shadow-ios">
        Noch keine erledigten Anfragen. Beantwortete Anfragen können in der Detailansicht als erledigt markiert
        werden – auch ohne Bestellung.
      </div>
    );
  }
  return (
    <div className="rounded-ios border border-dashed border-separator-opaque bg-card p-8 text-center shadow-ios">
      <p className="text-base font-semibold text-label">Alles erledigt – keine offenen Anfragen.</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-label-secondary">
        Preise oder Angebote bei einem Lieferanten anfragen – für ein Kundenangebot oder vor einer Bestellung.
      </p>
      <ol className="mx-auto mt-5 grid max-w-2xl gap-3 text-start text-sm sm:grid-cols-3">
        {[
          ["Lieferant & Positionen", "Lieferant wählen, Auftrag zuordnen, Artikel oder Freitext eintragen."],
          ["Prüfen & senden", "Die Mail wird automatisch aufgesetzt – kurz prüfen, Anhänge dazu, abschicken."],
          ["Antwort kommt hierher", "Antworten aus dem Postfach erscheinen an der Anfrage. Dann bestellen oder erledigen."],
        ].map(([title, text], idx) => (
          <li key={title} className="rounded-ios bg-fill-quaternary/40 p-3">
            <span className="mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-bold text-label-on-accent">
              {idx + 1}
            </span>
            <p className="font-semibold text-label">{title}</p>
            <p className="mt-0.5 text-xs text-label-secondary">{text}</p>
          </li>
        ))}
      </ol>
      <Button className="mt-6" size="lg" data-tour="anfrage-neu" onClick={onNew}>
        + Neue Anfrage
      </Button>
    </div>
  );
}

function AnfrageRow({
  row,
  stage,
  pendingCount,
  actions,
}: {
  row: AnfrageListItem;
  stage: AnfrageStage;
  pendingCount: number;
  actions: AnfragenListActions;
}) {
  const taNums = uniqueTaNummern(row);
  const auftraege = auftragLabels(row);
  const positions = (row.positionen || []).length;
  const groupSize = row.anfrage_group_id ? 1 + (row.group_siblings?.length || 0) : 0;
  const answers = Number(row.answer_count || 0);
  const tkNums = (row.tk_links || [])
    .map((l) => l.bestellnummern)
    .filter((v): v is string => !!v && v.trim() !== "");

  const timeline = (() => {
    switch (stage) {
      case "draft":
        return `Zuletzt bearbeitet ${fmtRelativeDays(row.updated_at) || fmtDateTime(row.updated_at)}`;
      case "ready":
        return `Erstellt ${fmtRelativeDays(row.created_at) || fmtDateTime(row.created_at)}`;
      case "waiting": {
        const since = row.last_sent_at || row.updated_at;
        return since ? `Versendet ${fmtRelativeDays(since)}` : "Versendet";
      }
      case "answered": {
        const when = row.last_answer_at ? fmtRelativeDays(row.last_answer_at) : "";
        return when ? `Antwort ${when}` : "Antwort erhalten";
      }
      case "ordered":
        return tkNums.length ? `Bestellung ${tkNums.join(", ")}` : "Bestellung erzeugt";
      case "done":
        return row.erledigt_at ? `Erledigt ${fmtRelativeDays(row.erledigt_at)}` : "Erledigt";
    }
  })();

  function primary(ev: MouseEvent) {
    ev.stopPropagation();
    if (stage === "draft") actions.onOpenDraft(row);
    else if (stage === "ready") actions.onSend(row);
    else actions.onOpen(row);
  }

  function open() {
    if (stage === "draft") actions.onOpenDraft(row);
    else actions.onOpen(row);
  }

  return (
    <li>
      <article
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(ev) => {
          if (ev.target !== ev.currentTarget) return;
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            open();
          }
        }}
        className={clsx(
          "group relative flex cursor-pointer flex-col gap-3 overflow-hidden rounded-ios border border-separator bg-card p-3 ps-4 shadow-ios transition sm:flex-row",
          "hover:border-accent/50 hover:shadow-ios-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft",
          stage === "answered" && "border-status-green/40",
        )}
      >
        <span
          className={clsx("absolute inset-y-0 start-0 w-1", stageAccentClass(stage))}
          aria-hidden
        />
        <span
          className={clsx(
            "mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-full sm:inline-flex",
            stageBadgeClass(stage),
          )}
        >
          <StageIcon stage={stage} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="flex min-w-0 items-center gap-2 text-base font-semibold text-label">
              <SupplierLogo
                name={row.supplier_name}
                partnerId={row.supplier_gp_id}
                hasLogo={row.supplier_has_logo}
                logoUpdatedAt={row.supplier_logo_updated_at}
                className="h-7 w-7 rounded-md text-[10px]"
              />
              <span className="truncate">{row.supplier_name || "Lieferant noch offen"}</span>
            </h3>
            <span
              className={clsx("rounded-full px-2 py-0.5 text-xs font-semibold", stageBadgeClass(stage))}
            >
              {stageLabel(stage)}
            </span>
            {pendingCount > 0 ? (
              <span className="rounded-full bg-status-green px-2 py-0.5 text-xs font-bold text-white">
                {pendingCount} neue {pendingCount === 1 ? "Antwort" : "Antworten"}
              </span>
            ) : answers > 0 && stage !== "done" ? (
              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label-secondary">
                {answers} {answers === 1 ? "Antwort" : "Antworten"} abgelegt
              </span>
            ) : null}
            {groupSize > 1 ? (
              <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium text-label-secondary">
                {groupSize} Firmen
              </span>
            ) : null}
          </div>

          <p className="mt-0.5 truncate text-sm text-label-secondary">
            {row.subject || (positions ? `${positions} ${positions === 1 ? "Position" : "Positionen"}` : "Ohne Betreff")}
          </p>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-label-tertiary">
            {taNums.length ? (
              <span className="font-mono text-label-secondary">{taNums.join(", ")}</span>
            ) : null}
            {auftraege.length ? <span>{auftraege.join(", ")}</span> : null}
            {positions && row.subject ? (
              <span>
                {positions} {positions === 1 ? "Position" : "Positionen"}
              </span>
            ) : null}
            <span>{timeline}</span>
          </div>
        </div>

        <div className="flex shrink-0 items-start sm:justify-end">
          <Button
            type="button"
            size="sm"
            className="w-full sm:w-auto"
            variant={stage === "answered" || stage === "ready" || stage === "draft" ? "primary" : "secondary"}
            onClick={primary}
          >
            {stagePrimaryAction(stage)}
          </Button>
        </div>
      </article>
    </li>
  );
}
